/**
 * Invoice API endpoints.
 *
 * Endpoints:
 * - GET /api/web-admin/invoices - List invoices with pagination and filters
 * - POST /api/web-admin/invoices/{id}/payment - Update payment status/date/method
 * - POST /api/web-admin/invoices/{id}/location - Update location
 * - POST /api/web-admin/invoices/{id}/is-vat-deductible - Update VAT deductible flag
 * - POST /api/web-admin/invoices/{id}/refresh-urls - Refresh presigned URLs for preview/download
 * - POST /api/web-admin/invoices/{id}/delete - Delete invoice (soft delete)
 * - POST /api/web-admin/invoices/{id}/duplicate-check - Check for duplicate documents
 * - POST /api/web-admin/invoices/{id}/verify - Mark invoice as verified
 * - POST /api/web-admin/invoices/{id}/unverify - Mark invoice as unverified
 */

import type { RequestOptions } from '@/types/api';
import type {
  InvoiceListParams,
  InvoiceListFilters,
  InvoiceListResponse,
  InvoiceDetailResponse,
  InvoiceViewCounts,
  InvoiceViewCountsResponse,
  InvoiceDetail,
  DocumentPage,
} from '@/types/invoices';

import { runBulkMutation, type BulkMutationResult } from './bulk-mutation';
import { api } from './client';

interface ApiPayloadResponse<T> {
  request_id: string;
  payload: T;
}

async function postPayload<T>(
  path: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>,
): Promise<ApiPayloadResponse<T>> {
  return api.webAdmin.post<ApiPayloadResponse<T>>(path, body, options);
}

type BackendDuplicateScenario =
  | 'exact_file'
  | 'same_invoice'
  | 'different_buyer'
  | 'different_number'
  | null;

interface BackendDuplicateCandidate {
  document_id: string;
  scenario: BackendDuplicateScenario;
  match_score?: number;
  confidence?: string;
  reference_number: string | null;
  supplier_name: string | null;
  total_amount: string | null;
  issue_date: string | null;
}

interface BackendDuplicateGateResponse {
  gate_level: string;
  needs_duplicate_gate: boolean;
  candidates: BackendDuplicateCandidate[];
  total?: number;
}

/**
 * Duplicate detection scenario types.
 *
 * A: Exact duplicate - same file hash
 * B: Same invoice
 * C: Same invoice data, different buyer
 * D: Same supplier/date/amount, different invoice number
 */
export type DuplicateScenario = 'A' | 'B' | 'C' | 'D';

/**
 * Duplicate candidate returned from duplicate check.
 */
export interface DuplicateCandidate {
  /** ID of the existing document */
  id: string;
  /** Duplicate scenario type (A-D) */
  scenario: DuplicateScenario;
  /** Similarity score (0-100, for scenario D) */
  similarity_score?: number;
  /** Reference number of existing document */
  reference_number: string | null;
  /** Supplier name of existing document */
  supplier_name: string | null;
  /** Total amount of existing document (Decimal as string) */
  total_amount: string | null;
  /** Currency of existing document */
  currency: string;
  /** Issue date of existing document */
  issue_date: string | null;
  /** Preview URL for thumbnail */
  preview_url: string | null;
}

/**
 * Response from duplicate check endpoint.
 */
export interface DuplicateCheckResponse {
  request_id: string;
  /** Whether duplicates were detected */
  has_duplicates: boolean;
  /** List of duplicate candidates */
  duplicates: DuplicateCandidate[];
  /** The current document's details for comparison */
  current: {
    id: string;
    reference_number: string | null;
    supplier_name: string | null;
    total_amount: string | null;
    currency: string;
    issue_date: string | null;
    preview_url: string | null;
  };
}

export interface ResolveKsefMatchRequest {
  resolution: 'confirmed' | 'dismissed';
}

export interface ResolveKsefMatchResponse {
  request_id: string;
  status: 'confirmed' | 'dismissed';
  document_id: string;
  target_document_id?: string;
  already_resolved?: boolean;
}

export interface UnlinkKsefMatchResponse {
  request_id: string;
  status: 'unlinked';
  document_id: string;
  related_document_id?: string;
}

export interface InvoiceOcrDebugResponse {
  request_id: string;
  raw_ocr_json: Record<string, unknown> | null;
}

/**
 * Response payload from location update endpoint.
 */
export interface LocationUpdatePayload {
  location_id: string | null;
  new_version: string;
}

/**
 * Response payload from expense category update endpoint.
 */
export interface ServiceDateUpdatePayload {
  service_date_override: string | null;
  new_version: string;
}

export interface ExpenseCategoryUpdatePayload {
  expense_category_id: string | null;
  new_version: string;
}

/**
 * Response payload from VAT deductible update endpoint.
 */
export interface VatDeductibleUpdatePayload {
  value: boolean;
  new_version: string;
}

/**
 * Response payload from refresh URLs endpoint.
 */
export interface RefreshUrlsPayload {
  pages: DocumentPage[];
}

/**
 * Response payload from delete endpoint.
 */
export interface InvoiceDeletePayload {
  status: string;
  new_version: string;
  deleted_at?: string;
  already_deleted?: boolean;
}

/**
 * Response payload from verify/unverify endpoints.
 */
export interface InvoiceVerifyPayload extends InvoiceDetail {
  new_version: string;
  already_verified?: boolean;
  already_unverified?: boolean;
}

/**
 * Single field override matching backend OverrideInput.
 */
export interface OverrideInput {
  field_key: string;
  value_kind: 'scalar' | 'entity';
  scalar_value?: unknown;
  entity_id?: string;
  entity_text?: string;
  override_reason?: string;
  action?: 'set' | 'clear';
}

/**
 * Request body for bulk override endpoint.
 */
export interface BulkOverrideRequest {
  overrides: OverrideInput[];
  expected_version: string;
  idempotency_key: string;
  payload_hash: string;
}

/**
 * Response from bulk override endpoint.
 */
export interface BulkOverrideResponse {
  request_id: string;
  item: InvoiceDetail;
}

/**
 * Default page size for invoice list requests.
 */
export const DEFAULT_INVOICE_LIMIT = 200;

/**
 * Serialize array filter values for API query params.
 * Arrays are joined with commas for backend parsing.
 */
function serializeFilterValue(value: string | string[] | undefined): string | undefined {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(',') : undefined;
  }
  return value;
}

/**
 * Calculate SHA-256 hash of a string using Web Crypto API.
 */
async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Entity fields that reference other tables (use value_kind: 'entity').
 */
const ENTITY_FIELDS = [
  'supplier_id',
  'location_id',
  'buyer_company_id',
  'payment_source_id',
  'expense_category_id',
];

export interface OverrideInputOptions {
  /** When value is null/undefined, prefer explicit null override instead of clear */
  preferExplicitNull?: boolean;
}

/**
 * Build an OverrideInput from a field name and value.
 * Automatically determines value_kind based on field name.
 */
export function buildOverrideInput(
  fieldKey: string,
  value: unknown,
  options: OverrideInputOptions = {},
): OverrideInput {
  if (ENTITY_FIELDS.includes(fieldKey)) {
    const base: OverrideInput = {
      field_key: fieldKey,
      value_kind: 'entity',
      // Null is an explicit unlink in the canonical current-state model. The
      // separate clear action only removes override metadata.
      action: 'set',
    };
    if (value) {
      base.entity_id = value as string;
    }
    return base;
  }

  const preferExplicitNull = options.preferExplicitNull ?? false;
  const isNullish = value === null || value === undefined;
  const base: OverrideInput = {
    field_key: fieldKey,
    value_kind: 'scalar',
    action: isNullish ? (preferExplicitNull ? 'set' : 'clear') : 'set',
  };
  if (!isNullish || preferExplicitNull) {
    base.scalar_value = isNullish ? null : value;
  }
  return base;
}

/**
 * Invoice API methods.
 */
export const invoicesApi = {
  /**
   * Get counts for each invoice view tab within the current scope filters.
   *
   * Scope filters narrow the dataset first (period/supplier/location/etc),
   * then tab formulas (inbox/to_pay/paid/overdue/deliveries) are applied on top.
   */
  getCounts: async (filters: InvoiceListFilters = {}): Promise<InvoiceViewCounts> => {
    const {
      document_id,
      supplier_id,
      location_id,
      status,
      doc_type,
      search,
      issue_date_from,
      issue_date_to,
      buyer_is_known,
    } = filters;
    const response = await api.webAdmin.get<InvoiceViewCountsResponse>('/invoices/counts', {
      params: {
        document_id,
        supplier_id,
        location_id,
        status: serializeFilterValue(status),
        doc_type: serializeFilterValue(doc_type),
        search,
        issue_date_from,
        issue_date_to,
        buyer_is_known,
      },
    });
    return response.counts;
  },

  /**
   * Get a single invoice by ID.
   *
   * @param id - Invoice ID (UUID)
   * @param includeDeleted - Whether to include deleted invoices
   * @returns Invoice detail response
   */
  getById: async (id: string, includeDeleted = false): Promise<InvoiceDetailResponse> => {
    return api.webAdmin.get<InvoiceDetailResponse>(
      `/invoices/${id}`,
      includeDeleted ? { params: { include_deleted: true } } : undefined,
    );
  },

  getOcrDebug: async (id: string): Promise<InvoiceOcrDebugResponse> => {
    return api.webAdmin.get<InvoiceOcrDebugResponse>(`/invoices/${id}/ocr-debug`);
  },

  /**
   * Get paginated list of invoices.
   *
   * @param params - Filter and pagination parameters
   * @returns Paginated invoice list with total count
   */
  list: async (params: InvoiceListParams = {}): Promise<InvoiceListResponse> => {
    const {
      limit = DEFAULT_INVOICE_LIMIT,
      offset = 0,
      document_id,
      supplier_id,
      location_id,
      status,
      doc_type,
      payment_status,
      include_deleted,
      include_merged,
      buyer_is_known,
      is_verified,
      exclude_overdue,
      search,
      issue_date_from,
      issue_date_to,
      sort_by,
      sort_order,
    } = params;

    return api.webAdmin.get<InvoiceListResponse>('/invoices', {
      params: {
        limit,
        offset,
        document_id,
        supplier_id,
        location_id,
        status: serializeFilterValue(status),
        doc_type: serializeFilterValue(doc_type),
        payment_status: serializeFilterValue(payment_status),
        include_deleted,
        include_merged,
        buyer_is_known,
        is_verified,
        exclude_overdue,
        search,
        issue_date_from,
        issue_date_to,
        sort_by,
        sort_order,
      },
    });
  },

  /**
   * Update location for an invoice.
   *
   * @param id - Invoice ID
   * @param locationId - Location ID (null to clear)
   * @returns Updated invoice
   */
  updateLocation: async (id: string, locationId: string | null): Promise<LocationUpdatePayload> => {
    const response = await postPayload<LocationUpdatePayload>(`/invoices/${id}/location`, {
      location_id: locationId,
    });
    return response.payload;
  },

  /**
   * Update expense category for an invoice.
   *
   * @param id - Invoice ID
   * @param expenseCategoryId - Expense category ID (null to clear)
   * @returns Updated invoice
   */
  updateExpenseCategory: async (
    id: string,
    expenseCategoryId: string | null,
  ): Promise<ExpenseCategoryUpdatePayload> => {
    const response = await postPayload<ExpenseCategoryUpdatePayload>(
      `/invoices/${id}/expense-category`,
      {
        expense_category_id: expenseCategoryId,
      },
    );
    return response.payload;
  },

  /**
   * Set or clear the service date the P&L recognizes the invoice by (owner 2026-11-03).
   *
   * @param id - Invoice ID
   * @param serviceDate - ISO date, or null to fall back to KSeF / issue date
   */
  updateServiceDate: async (
    id: string,
    serviceDate: string | null,
  ): Promise<ServiceDateUpdatePayload> => {
    const response = await postPayload<ServiceDateUpdatePayload>(`/invoices/${id}/service-date`, {
      service_date_override: serviceDate,
    });
    return response.payload;
  },

  /**
   * Update VAT deductible flag for an invoice.
   *
   * @param id - Invoice ID
   * @param isVatDeductible - Whether the invoice is VAT deductible
   * @returns Updated invoice
   */
  updateVatDeductible: async (
    id: string,
    isVatDeductible: boolean,
  ): Promise<VatDeductibleUpdatePayload> => {
    const response = await postPayload<VatDeductibleUpdatePayload>(
      `/invoices/${id}/is-vat-deductible`,
      {
        value: isVatDeductible,
      },
    );
    return response.payload;
  },

  /**
   * Delete an invoice (soft delete).
   *
   * @param id - Invoice ID
   * @returns Success response
   */
  delete: async (id: string): Promise<InvoiceDeletePayload> => {
    const response = await postPayload<InvoiceDeletePayload>(`/invoices/${id}/delete`, {});
    return response.payload;
  },

  /**
   * Delete several invoices through the shared bulk-write rail (bounded
   * concurrency, per-item outcome with the real error).
   */
  bulkDelete: (
    ids: readonly string[],
    onProgress?: (processed: number, total: number) => void,
  ): Promise<BulkMutationResult<string, InvoiceDeletePayload>> =>
    runBulkMutation(ids, (id) => invoicesApi.delete(id), onProgress ? { onProgress } : {}),

  /**
   * Bulk update field overrides for an invoice.
   *
   * Used to update multiple detail-only fields in a single request.
   * Automatically generates idempotency key and payload hash.
   *
   * @param id - Invoice ID
   * @param overrides - Array of override inputs
   * @param expectedVersion - Current invoice version for optimistic locking
   * @returns Updated invoice detail
   */
  bulkOverride: async (
    id: string,
    overrides: OverrideInput[],
    expectedVersion: string,
  ): Promise<BulkOverrideResponse> => {
    // Calculate payload hash (SHA-256 of sorted JSON)
    const sortedOverrides = [...overrides].sort((a, b) => a.field_key.localeCompare(b.field_key));
    const payloadForHash = JSON.stringify({ overrides: sortedOverrides });
    const payloadHash = await sha256(payloadForHash);

    // Generate deterministic idempotency key for safe retries
    const idempotencyKey = `bulk-override:${id}:${expectedVersion}:${payloadHash}`;

    const request: BulkOverrideRequest = {
      overrides,
      expected_version: expectedVersion,
      idempotency_key: idempotencyKey,
      payload_hash: payloadHash,
    };

    const response = await postPayload<BulkOverrideResponse>(
      `/invoices/${id}/overrides/bulk`,
      request,
      { idempotencyKey },
    );
    return response.payload;
  },

  /**
   * Refresh presigned URLs for an invoice's preview and download links.
   *
   * Presigned URLs have limited lifetime. Call this endpoint when URLs expire
   * to get fresh, valid URLs for viewing/downloading invoice documents.
   *
   * @param id - Invoice ID
   * @returns Updated invoice detail with fresh presigned URLs
   */
  refreshUrls: async (id: string): Promise<RefreshUrlsPayload> => {
    const response = await postPayload<RefreshUrlsPayload>(`/invoices/${id}/refresh-urls`, {});
    return response.payload;
  },

  /**
   * Check for duplicate documents.
   *
   * Detects duplicate candidates using the backend duplicate gate:
   * - Scenario A: Exact file hash match
   * - Scenario B: Same invoice (supplier + reference + date + amount)
   * - Scenario C: Same invoice data, but buyer differs
   * - Scenario D: Same supplier/date/amount, but invoice number differs
   *
   * @param id - Invoice ID to check
   * @returns Duplicate check response with candidates
   */
  duplicateCheck: async (id: string): Promise<DuplicateCheckResponse> => {
    const response = await postPayload<BackendDuplicateGateResponse>(
      `/invoices/${id}/duplicate-check`,
      {},
    );
    const currentDetail = await invoicesApi.getById(id);
    const currentItem = currentDetail.item;
    const currentPreview = currentItem.pages.find((page) => page.preview_url)?.preview_url ?? null;

    const current = {
      id: currentItem.id,
      reference_number: currentItem.reference_number,
      supplier_name: currentItem.supplier_name,
      total_amount: currentItem.total_amount,
      currency: currentItem.currency,
      issue_date: currentItem.issue_date,
      preview_url: currentPreview,
    };

    const candidates = response.payload.candidates;
    if (candidates.length === 0) {
      return {
        request_id: response.request_id,
        has_duplicates: false,
        duplicates: [],
        current,
      };
    }

    const candidateDetails = await Promise.all(
      candidates.map(async (candidate) => {
        try {
          const detail = await invoicesApi.getById(candidate.document_id);
          return detail.item;
        } catch {
          return null;
        }
      }),
    );

    const mapScenario = (scenario: BackendDuplicateScenario): DuplicateScenario | null => {
      switch (scenario) {
        case 'exact_file':
          return 'A';
        case 'same_invoice':
          return 'B';
        case 'different_buyer':
          return 'C';
        case 'different_number':
          return 'D';
        default:
          return null;
      }
    };

    const duplicates = candidates.flatMap((candidate, index) => {
      const scenario = mapScenario(candidate.scenario);
      if (!scenario) {
        return [];
      }
      const detail = candidateDetails[index];
      const previewUrl = detail?.pages.find((page) => page.preview_url)?.preview_url ?? null;
      const confidence = candidate.confidence ? Number(candidate.confidence) : NaN;
      const similarityScore = Number.isFinite(confidence)
        ? Math.round(confidence * 100)
        : undefined;

      return [
        {
          id: candidate.document_id,
          scenario,
          ...(similarityScore !== undefined && { similarity_score: similarityScore }),
          reference_number: detail?.reference_number ?? candidate.reference_number ?? null,
          supplier_name: detail?.supplier_name ?? candidate.supplier_name ?? null,
          total_amount: detail?.total_amount ?? candidate.total_amount ?? null,
          currency: detail?.currency ?? current.currency,
          issue_date: detail?.issue_date ?? candidate.issue_date ?? null,
          preview_url: previewUrl,
        },
      ];
    });

    return {
      request_id: response.request_id,
      has_duplicates: duplicates.length > 0,
      duplicates,
      current,
    };
  },

  resolveKsefMatch: async (
    id: string,
    request: ResolveKsefMatchRequest,
  ): Promise<ResolveKsefMatchResponse> => {
    const response = await postPayload<{
      status: 'confirmed' | 'dismissed';
      document_id: string;
      target_document_id?: string;
      already_resolved?: boolean;
    }>(`/invoices/${id}/resolve-ksef-match`, request);

    const payload = response.payload;
    return {
      request_id: response.request_id,
      status: payload.status,
      document_id: payload.document_id,
      ...(payload.target_document_id !== undefined
        ? { target_document_id: payload.target_document_id }
        : {}),
      ...(payload.already_resolved !== undefined
        ? { already_resolved: payload.already_resolved }
        : {}),
    };
  },

  unlinkKsefMatch: async (id: string): Promise<UnlinkKsefMatchResponse> => {
    const response = await postPayload<{
      status: 'unlinked';
      document_id: string;
      related_document_id?: string;
    }>(`/invoices/${id}/unlink-ksef-match`);

    const payload = response.payload;
    return {
      request_id: response.request_id,
      status: payload.status,
      document_id: payload.document_id,
      ...(payload.related_document_id !== undefined
        ? { related_document_id: payload.related_document_id }
        : {}),
    };
  },

  /**
   * Mark an invoice as verified.
   *
   * Sets the is_verified flag to true and updates status to VERIFIED.
   *
   * @param id - Invoice ID
   * @returns Updated invoice detail
   */
  verify: async (id: string): Promise<InvoiceVerifyPayload> => {
    const response = await postPayload<InvoiceVerifyPayload>(`/invoices/${id}/verify`, {});
    return response.payload;
  },

  /**
   * Mark an invoice as unverified.
   *
   * Sets the is_verified flag to false and updates status back to DRAFT.
   *
   * @param id - Invoice ID
   * @returns Updated invoice detail
   */
  unverify: async (id: string): Promise<InvoiceVerifyPayload> => {
    const response = await postPayload<InvoiceVerifyPayload>(`/invoices/${id}/unverify`, {});
    return response.payload;
  },
};
